<template>
  <div id="app">
    <transition name="app-bootstrap-fade" mode="out-in">
      <main
        v-if="isBootstrapping"
        key="bootstrap"
        class="app-bootstrap"
        aria-label="Avvio di Fusco Fit Connect"
      >
        <div class="app-bootstrap__content">
          <img
            class="app-bootstrap__logo"
            :src="brandLogo"
            alt="Fusco Fit"
          />
          <div class="app-bootstrap__status" role="status" aria-live="polite">
            <span class="app-bootstrap__spinner" aria-hidden="true"></span>
            <span>Prepariamo il tuo percorso</span>
          </div>
        </div>
      </main>
      <router-view v-else key="application"></router-view>
    </transition>
  </div>
</template>

<script>
import { Capacitor } from '@capacitor/core';
import { auth, authPersistenceReady } from '@/firebaseConfig';
import brandLogo from '../fuscofit/assets/logo-black.png';

const AUTH_BOOTSTRAP_TIMEOUT_MS = 5000;

const waitForAuthState = async () => {
  let timeoutId;
  try {
    await authPersistenceReady;
    await Promise.race([
      auth.authStateReady(),
      new Promise(resolve => {
        timeoutId = window.setTimeout(resolve, AUTH_BOOTSTRAP_TIMEOUT_MS);
      }),
    ]);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export default {
  name: 'App',
  data() {
    return {
      brandLogo,
      isBootstrapping: Capacitor.isNativePlatform(),
    };
  },
  async mounted() {
    if (!this.isBootstrapping) return;

    try {
      await waitForAuthState();

      if (auth.currentUser && this.$route.path === '/') {
        const sameStoredUser = localStorage.getItem('authUid') === auth.currentUser.uid;
        const storedRole = sameStoredUser
          ? (localStorage.getItem('userRole') || 'User')
          : 'User';
        const destination = this.initialDestination(storedRole);
        await this.$router.replace(destination).catch(() => {});
      }
    } catch (error) {
      console.warn('Ripristino iniziale della sessione non riuscito:', error);
    } finally {
      this.isBootstrapping = false;
    }
  },
  methods: {
    initialDestination(userRole) {
      if (this.$route.query.redirect === '/i-miei-allenamenti') return '/i-miei-allenamenti';
      if (userRole === 'Amministratore' || userRole === 'Studio') return '/dashboard';
      if (userRole === 'Spedizioni') return '/order-list';
      return '/user';
    },
  },
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

#app {
  font-family: 'Montserrat', Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #111111;
}

.app-bootstrap {
  position: fixed;
  z-index: 10000;
  inset: 0;
  display: grid;
  place-items: center;
  min-height: 100vh;
  min-height: 100svh;
  padding:
    max(24px, env(safe-area-inset-top))
    max(24px, env(safe-area-inset-right))
    max(24px, env(safe-area-inset-bottom))
    max(24px, env(safe-area-inset-left));
  box-sizing: border-box;
  overflow: hidden;
  background:
    radial-gradient(circle at 50% 38%, rgba(213, 11, 53, 0.09), transparent 30%),
    linear-gradient(145deg, #ffffff 0%, #f6f3f0 100%);
}

.app-bootstrap__content {
  display: grid;
  justify-items: center;
  gap: 30px;
  width: min(100%, 300px);
}

.app-bootstrap__logo {
  display: block;
  width: min(54vw, 210px);
  height: auto;
  object-fit: contain;
}

.app-bootstrap__status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 11px;
  min-height: 24px;
  color: #5f5a57;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: 0.035em;
  text-align: center;
}

.app-bootstrap__spinner {
  width: 17px;
  height: 17px;
  flex: 0 0 17px;
  box-sizing: border-box;
  border: 2px solid rgba(213, 11, 53, 0.2);
  border-top-color: #d50b35;
  border-radius: 50%;
  animation: app-bootstrap-spin 0.75s linear infinite;
}

.app-bootstrap-fade-enter-active,
.app-bootstrap-fade-leave-active {
  transition: opacity 180ms ease;
}

.app-bootstrap-fade-enter-from,
.app-bootstrap-fade-leave-to {
  opacity: 0;
}

@keyframes app-bootstrap-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-bootstrap__spinner {
    animation-duration: 1.5s;
  }

  .app-bootstrap-fade-enter-active,
  .app-bootstrap-fade-leave-active {
    transition-duration: 0.01ms;
  }
}

</style>
